import { create } from 'zustand';
import type { Alarm } from '../types/alarm';
import type { AlarmStats } from '../types/device';
import * as alarmService from '../services/alarmService';
import useAuthStore from './authStore';
import alarmSoundService, { getHighestSeverity } from '../services/alarmSoundService';

/** 根據當前使用者角色與 alarmRoleFilter 計算需排除的警報類型
 *
 * ⚠️ 2026-10-23：判定條件由 `!== true` 改為 `=== false`。
 *    矩陣畫面把「沒填」顯示成已勾選，所以沒填必須當成可見；
 *    原本的寫法會讓「只取消過任一格」的類型對所有未明確勾選的角色消失。
 *    與 backend/app/services/alarm_role_filter.py、AlarmPage、Header、
 *    constants/permissions.ts 共五處同步，改一處會造成畫面與後端不一致。
 */
function computeExcludedTypes(): string | undefined {
  const { user, alarmRoleFilter } = useAuthStore.getState();
  if (!user || (user.role_level ?? 99) <= 1) return undefined; // dev 不過濾；admin 依 alarmRoleFilter
  if (!alarmRoleFilter || Object.keys(alarmRoleFilter).length === 0) return undefined;
  const excluded = Object.entries(alarmRoleFilter)
    .filter(([, roleMap]) => roleMap?.[user.role] === false)
    .map(([type]) => type);
  return excluded.length > 0 ? excluded.join(',') : undefined;
}

interface AlarmState {
  activeAlarms: Alarm[];
  stats: AlarmStats | null;
  loading: boolean;
  fetchActiveAlarms: () => Promise<void>;
  fetchStats: () => Promise<void>;
  setActiveAlarms: (alarms: Alarm[]) => void;
  addNewAlarms: (alarms: Alarm[]) => void;
}

const useAlarmStore = create<AlarmState>((set, get) => ({
  activeAlarms: [],
  stats: null,
  loading: false,

  fetchActiveAlarms: async () => {
    try {
      const alarms = await alarmService.getActiveAlarms();
      set({ activeAlarms: alarms });
    } catch {
      // 靜默失敗
    }
  },

  fetchStats: async () => {
    // 權限未載入前不更新統計，避免快取到未過濾的數據導致數字閃爍
    const { user, permissionsLoaded } = useAuthStore.getState();
    if (!user || !permissionsLoaded) return;
    try {
      const stats = await alarmService.getAlarmStats(computeExcludedTypes());
      set({ stats });
    } catch {
      // 靜默失敗
    }
  },

  setActiveAlarms: (alarms) => set({ activeAlarms: alarms }),

  addNewAlarms: (newAlarms) => {
    const existing = get().activeAlarms;
    const existingIds = new Set(existing.map((a) => a.id));
    const fresh = newAlarms.filter((a) => !existingIds.has(a.id));
    if (fresh.length > 0) {
      set({ activeAlarms: [...fresh, ...existing].slice(0, 100) });
      // 播放最高嚴重度的警報音效
      alarmSoundService.play(getHighestSeverity(fresh));
    }
  },
}));

export default useAlarmStore;
